import {Container} from '@mui/material';
import React, {useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useMatch, useParams} from 'react-router-dom';
import {getChildProfile} from '../../services/familyService';
import {getPlayerProfile} from '../../services/teamService';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {getPlayerSessionsAsync} from '../../store/playerSessionSlice';
import {getBaselinesAsync} from '../../store/baselineSlice';
import {SessionTypes} from '../../enums';
import BackButton from '../shared/BackButton';
import PageTitle from '../shared/PageTitle';
import PlayerSessionsPanel from '../shared/PlayerSessionsPanel';
import ProfileIdentity from '../shared/ProfileIdentity';
import PageTitleContainer from '../shared/PageTitleContainer';
import PlayerSessionFilter from '../shared/PlayerSessionFilter';
import usePlayerSessionFilter from '../../hooks/usePlayerSessionFilter';
import {Profile} from '../../interfaces';

interface Props {}

const PlayerSessions: React.FC<Props> = () => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const currentState = useAppSelector(state => ({
    family: state.family,
    team: state.team,
  }));
  const baselines = useAppSelector(state => state.baseline?.baselines);
  const {
    filteredPlayerSessions,
    teams: playerSessionTeams,
    filter,
    loading: playerSessionsLoading,
    onFilterUpdate,
  } = usePlayerSessionFilter();

  const {profileId, teamId} = useParams();
  const profileIdInt = parseInt(profileId ?? '');
  const teamIdInt = parseInt(teamId ?? '');
  const isAdminView = !!useMatch('/admin/profiles/:profileId/playersessions');

  // allow filtering player sessions by specific player
  // this allows coach to only see player sessions that are part of their team sessions
  // const playerId: number | null = useMemo(() => {
  //   if (!teamId || !profileId) {
  //     return null;
  //   }
  //   const team = currentState?.team?.teams?.find(
  //     (tm: Team) => tm.id === parseInt(teamId),
  //   );
  //   const player = team?.players?.find(
  //     (p: Player) => p.profile?.id === parseInt(profileId),
  //   );
  //   return player?.id || null;
  // }, [currentState?.team?.teams, profileId, teamId]);

  useEffect(() => {
    dispatch(
      getPlayerSessionsAsync({
        profileId: profileId ? profileIdInt : undefined,
        // playerId: playerId || undefined,
      }),
    );
    if (profileId) {
      dispatch(
        getBaselinesAsync({
          profileId: profileIdInt,
          types: Object.values(SessionTypes),
        }),
      );
    }
  }, [dispatch, profileId, profileIdInt]);

  const title = t('pages.playerSessions');
  const childProfile: Profile | null = getChildProfile({
    family: currentState?.family?.families?.[0],
    profileId: profileIdInt,
  });
  const playerProfile = getPlayerProfile({
    team: (currentState?.team?.teams ?? []).filter(
      team => team.id === teamIdInt,
    )[0],
    profileId: profileIdInt,
  });

  let backText = t('navigation.back', {pageName: t('pages.dashboard')});
  let backUrl = '/';
  let navigateUrlPrefix = '';
  let groupNavigateUrlPrefix: string | undefined;

  if (isAdminView) {
    // Session detail links use the player's own routes so the admin sees the
    // sessions as if they were the player. Grouped sessions need the profile
    // id in the path to fetch the right player's group, so they stay on the
    // admin route.
    backText = t('navigation.back', {
      pageName: t('pages.profileDetail', {id: profileId}),
    });
    backUrl = `/admin/profiles/${profileId}`;
    navigateUrlPrefix = '/playersessions';
    groupNavigateUrlPrefix = `/admin/profiles/${profileId}/playersessions`;
  } else if (teamId) {
    backText = t('navigation.back', {pageName: t('pages.teamPlayers')});
    backUrl = `/teams/${teamId}/players`;
    navigateUrlPrefix = `/teams/${teamId}/players/${profileId}/playersessions`;
  } else if (profileId) {
    navigateUrlPrefix = `/family/children/${profileId}/playersessions`;
  }

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {pageName: t('pages.playerSessions')})}
        </title>
      </Helmet>
      <Container>
        <PageTitleContainer>
          <PageTitle noBottomMargin>{title}</PageTitle>
          <PlayerSessionFilter
            onFilterUpdate={onFilterUpdate}
            currentFilters={filter}
            teams={playerSessionTeams ?? []}
          />
        </PageTitleContainer>
        <BackButton navigationUrl={backUrl}>{backText}</BackButton>
        {childProfile && (
          <ProfileIdentity
            firstName={childProfile?.firstName}
            lastName={childProfile?.lastName}
            position={childProfile?.position}
            profilePictureUrl={childProfile?.profilePictureUrl}
          />
        )}
        {playerProfile && (
          <ProfileIdentity
            firstName={playerProfile?.firstName}
            lastName={playerProfile?.lastName}
            position={playerProfile?.position}
            profilePictureUrl={playerProfile?.profilePictureFileKey}
          />
        )}
        <PlayerSessionsPanel
          navigateUrlPrefix={navigateUrlPrefix}
          groupNavigateUrlPrefix={groupNavigateUrlPrefix}
          playerSessions={filteredPlayerSessions}
          baselines={baselines}
          loading={playerSessionsLoading}
        />
      </Container>
    </>
  );
};

export default PlayerSessions;
